import type { Envelope } from "../types";
import { SUPPORTED_LICENSES } from "./licenses";

export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly field?: string,
  ) {
    super(message);
    this.name = "ValidationError";
  }
}

const E164 = /^\+[1-9]\d{1,14}$/;
const HEX64 = /^[0-9a-f]{64}$/;
const STAMP_ID = /^[A-Z2-7]{9}$/;
const ISO_8601_UTC_MS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

const KINDS = new Set(["stamp", "index", "supersession", "identity_link"]);
const CLAIM_TYPES = new Set(["authorship", "in_my_possession"]);
const SOURCES = new Set(["draft", "forward_self", "forward_other", "export"]);
const LICENSES = new Set(SUPPORTED_LICENSES);

function check(condition: unknown, message: string, field?: string): void {
  if (!condition) {
    throw new ValidationError(message, field);
  }
}

function isString(v: unknown): v is string {
  return typeof v === "string";
}

function isInt(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v);
}

export function validateEnvelope(env: Envelope): void {
  check(env.v === 1, "v must be 1", "v");

  check(
    isString(env.stamp_id) && STAMP_ID.test(env.stamp_id),
    "stamp_id must be 9 base32 chars",
    "stamp_id",
  );
  check(
    isInt(env.stamp_id_salt) && env.stamp_id_salt >= 0 && env.stamp_id_salt <= 4,
    "stamp_id_salt must be an integer in [0, 4]",
    "stamp_id_salt",
  );

  check(KINDS.has(env.kind as string), `unknown kind: ${env.kind}`, "kind");
  check(
    CLAIM_TYPES.has(env.claim_type as string),
    `unknown claim_type: ${env.claim_type}`,
    "claim_type",
  );
  check(SOURCES.has(env.source as string), `unknown source: ${env.source}`, "source");

  check(
    isString(env.sender?.phone_e164) && E164.test(env.sender.phone_e164),
    "sender.phone_e164 must be E.164",
    "sender.phone_e164",
  );
  check(isString(env.sender?.display_name), "sender.display_name required", "sender.display_name");
  check(
    isString(env.sender?.wa_pushname_at_stamp),
    "sender.wa_pushname_at_stamp required",
    "sender.wa_pushname_at_stamp",
  );

  check(isString(env.content?.text), "content.text required", "content.text");
  check(
    isString(env.content?.sha256) && HEX64.test(env.content.sha256),
    "content.sha256 must be 64 hex chars",
    "content.sha256",
  );
  check(
    isInt(env.content?.byte_length) && env.content.byte_length >= 0,
    "content.byte_length must be a non-negative integer",
    "content.byte_length",
  );
  check(
    env.content?.language_hint === null || isString(env.content?.language_hint),
    "content.language_hint must be string|null",
    "content.language_hint",
  );

  check(LICENSES.has(env.license), `unsupported license: ${env.license}`, "license");

  check(
    isString(env.stamped_at) && ISO_8601_UTC_MS.test(env.stamped_at),
    "stamped_at must be ISO-8601 UTC ms",
    "stamped_at",
  );
  check(
    env.original_sent_at === null ||
      (isString(env.original_sent_at) && ISO_8601_UTC_MS.test(env.original_sent_at)),
    "original_sent_at must be ISO-8601 UTC ms or null",
    "original_sent_at",
  );

  check(env.issuer?.service === "sigil", "issuer.service must be 'sigil'", "issuer.service");
  check(isString(env.issuer?.instance), "issuer.instance required", "issuer.instance");
  check(isString(env.issuer?.key_id), "issuer.key_id required", "issuer.key_id");

  const ctx = env.context ?? ({} as typeof env.context);
  if (ctx.chat_export_sha256 !== null) {
    check(
      isString(ctx.chat_export_sha256) && HEX64.test(ctx.chat_export_sha256),
      "context.chat_export_sha256 must be 64 hex chars",
      "context.chat_export_sha256",
    );
  }
  if (ctx.supersedes !== null) {
    check(
      isString(ctx.supersedes) && STAMP_ID.test(ctx.supersedes),
      "context.supersedes must be 9 base32 chars",
      "context.supersedes",
    );
  }
  if (ctx.parent_index !== null) {
    check(
      isString(ctx.parent_index) && STAMP_ID.test(ctx.parent_index),
      "context.parent_index must be 9 base32 chars",
      "context.parent_index",
    );
  }
  if (ctx.linked_phone !== null) {
    check(
      isString(ctx.linked_phone) && E164.test(ctx.linked_phone),
      "context.linked_phone must be E.164",
      "context.linked_phone",
    );
  }
  if (ctx.verification_commitment !== null) {
    check(
      isString(ctx.verification_commitment) && HEX64.test(ctx.verification_commitment),
      "context.verification_commitment must be 64 hex chars",
      "context.verification_commitment",
    );
  }
  if (ctx.children !== null) {
    check(Array.isArray(ctx.children), "context.children must be array|null", "context.children");
    for (const c of ctx.children) {
      check(
        isString(c) && STAMP_ID.test(c),
        `context.children[]: ${c} must be 9 base32 chars`,
        "context.children",
      );
    }
  }

  if (env.kind === "index") {
    check(
      Array.isArray(ctx.children) && ctx.children.length > 0,
      "kind='index' requires context.children to be a non-empty list",
      "context.children",
    );
    check(
      ctx.chat_export_sha256 !== null,
      "kind='index' requires context.chat_export_sha256",
      "context.chat_export_sha256",
    );
  }
  if (env.kind === "supersession") {
    check(
      ctx.supersedes !== null,
      "kind='supersession' requires context.supersedes",
      "context.supersedes",
    );
  }
  if (env.kind === "identity_link") {
    check(
      ctx.linked_phone !== null,
      "kind='identity_link' requires context.linked_phone",
      "context.linked_phone",
    );
    check(
      ctx.verification_commitment !== null,
      "kind='identity_link' requires context.verification_commitment",
      "context.verification_commitment",
    );
  }
  if (env.source === "export") {
    check(
      env.original_sent_at !== null,
      "source='export' requires original_sent_at",
      "original_sent_at",
    );
    check(
      ctx.chat_export_sha256 !== null,
      "source='export' requires context.chat_export_sha256",
      "context.chat_export_sha256",
    );
  }
}
